/**
 * UserDrawer — Add / Edit user modal (May 2026 Figma).
 *
 * NOTE: Legacy filename retained so the parent UsersPage import path doesn't
 * break, but this is now a centered modal that matches the same .os-*
 * shell used by Site / Talk group / Team modals.
 *
 * Props:
 *   open    : boolean
 *   user    : existing employee → EDIT mode; null/undefined → ADD mode
 *   onClose : () => void
 *   onSaved : () => void   — fired after a successful create/update
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import userService from '../../services/userService';
import organizationService from '../../services/organizationService';
import employeeRoleService from '../../services/employeeRoleService';
import teamsService from '../../services/teamsService';
import { useTenant } from '../../context/TenantContext';

const DEFAULT_FORM = {
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  email: '',
  phoneCode: '+1',
  phone: '',
  timezone: '',
  startDate: '',
  endDate: '',
  department: '',
  departmentId: '',
  employeeId: '',
  externalId: '',
  status: 'ACTIVE',
  description: '',
  pairingPin: '',
  profileImage: null,
};

const DEFAULT_ASSIGNMENTS = {
  siteIds: [],
  roleIds: [],
  teamIds: [],
  extraTalkGroupIds: [],
  homeTalkGroupId: '',
};

const STATUSES = [
  { value: 'ACTIVE',    label: 'Active'    },
  { value: 'INACTIVE',  label: 'Inactive'  },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const TIMEZONES = [
  { value: '',                    label: 'Select time zone' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Pacific Time' },
  { value: 'America/Denver',      label: '(GMT-07:00) Mountain Time' },
  { value: 'America/Chicago',     label: '(GMT-06:00) Central Time' },
  { value: 'America/New_York',    label: '(GMT-05:00) Eastern Time' },
  { value: 'UTC',                 label: '(GMT+00:00) UTC' },
  { value: 'Europe/London',       label: '(GMT+00:00) London' },
  { value: 'Europe/Berlin',       label: '(GMT+01:00) Berlin' },
  { value: 'Asia/Kolkata',        label: '(GMT+05:30) India Standard Time' },
  { value: 'Asia/Singapore',      label: '(GMT+08:00) Singapore' },
  { value: 'Asia/Tokyo',          label: '(GMT+09:00) Tokyo' },
];

const PHONE_CODES = [
  { value: '+1',  label: '🇺🇸 +1' },
  { value: '+44', label: '🇬🇧 +44' },
  { value: '+91', label: '🇮🇳 +91' },
  { value: '+61', label: '🇦🇺 +61' },
  { value: '+49', label: '🇩🇪 +49' },
];

function HelpIcon() {
  return <i className="far fa-question-circle os-help-icon" aria-hidden="true" />;
}

function extractOptionValue(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') {
    return raw.roleId ?? raw.id ?? raw.value ?? null;
  }
  return raw;
}

function extractOptionLabel(raw) {
  if (raw == null) return '';
  if (typeof raw === 'object') {
    return raw.roleName ?? raw.name ?? raw.label ?? raw.title ?? '';
  }
  return String(raw);
}

function normalizeRoleIds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => extractOptionValue(item))
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function normalizeRoleIdsResponse(payload) {
  if (Array.isArray(payload)) return normalizeRoleIds(payload);
  if (Array.isArray(payload?.content)) return normalizeRoleIds(payload.content);
  if (Array.isArray(payload?.items)) return normalizeRoleIds(payload.items);
  return [];
}

/** Reusable multi/single-select dropdown for the Assignments tab. */
function MultiSelect({ placeholder, options, value, onChange, single = false, disabled, caption = 'SELECT FROM THE LIST', selectAllLabel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = single ? (value ? [value] : []) : (Array.isArray(value) ? value : []);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? options : options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, search]);

  const optionKey = (raw) => String(extractOptionValue(raw) ?? raw);
  const selectedKeys = selected.map(optionKey);
  const allKeys = options.map((o) => optionKey(o.value));
  const displayFor = (raw) => {
    const key = optionKey(raw);
    const option = options.find((x) => optionKey(x.value) === key);
    if (option?.label != null && option.label !== '') return String(option.label);
    const fallback = extractOptionLabel(raw);
    if (fallback) return fallback;
    return key;
  };
  const allSelected   = !single && options.length > 0 && allKeys.every((k) => selectedKeys.includes(k));
  const someSelected  = !single && selectedKeys.length > 0 && !allSelected;
  const toggleAll = () => {
    if (single) return;
    onChange(allSelected ? [] : options.map((o) => o.value));
  };

  const triggerLabel = (() => {
    if (selected.length === 0) return placeholder;
    if (single) {
      return displayFor(selected[0]);
    }
    return null; // chips rendered separately for multi-mode
  })();

  const removeChip = (e, v) => {
    e.stopPropagation();
    if (single) { onChange(null); return; }
    const target = optionKey(v);
    onChange(selected.filter((x) => optionKey(x) !== target));
  };

  const toggle = (v) => {
    if (single) { onChange(v); setOpen(false); return; }
    const k = optionKey(v);
    const next = selectedKeys.includes(k)
      ? selected.filter((x) => optionKey(x) !== k)
      : [...selected, v];
    onChange(next);
  };

  return (
    <div className={`sites-mselect um-mselect ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`} ref={ref}>
      <button
        type="button"
        className="sites-mselect-trigger um-mselect-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <div className="um-chips">
          {!single && selected.length > 0 && selected.map((v, idx) => {
            const chipLabel = displayFor(v);
            return (
              <span key={`${optionKey(v)}-${idx}`} className="um-chip">
                {chipLabel}
                <span
                  role="button"
                  tabIndex={0}
                  className="um-chip-x"
                  onClick={(e) => removeChip(e, v)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') removeChip(e, v); }}
                  aria-label={`Remove ${chipLabel}`}
                >
                  <i className="fas fa-times" />
                </span>
              </span>
            );
          })}
          {single && selected.length > 0 && (
            <span className="um-single-value">{triggerLabel}</span>
          )}
          {selected.length === 0 && (
            <span className="sites-mselect-placeholder">{placeholder}</span>
          )}
        </div>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} um-mselect-caret`} />
      </button>
      {open && (
        <div className="sites-mselect-menu">
          <div className="sites-mselect-caption">{caption}</div>
          <div className="tg-sites-search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {!single && selectAllLabel && options.length > 0 && (
            <button
              type="button"
              className="sites-mselect-row sites-mselect-row-all"
              onClick={toggleAll}
            >
              <span className={`sites-mselect-box ${allSelected ? 'checked' : ''} ${someSelected ? 'indeterminate' : ''}`}>
                {allSelected && <i className="fas fa-check" />}
                {someSelected && <i className="fas fa-minus" />}
              </span>
              <span className="sites-mselect-label sites-mselect-label-strong">{selectAllLabel}</span>
            </button>
          )}
          {filtered.length === 0 && <div className="sites-mselect-empty">No options</div>}
          {filtered.map((o) => {
            const checked = selectedKeys.includes(optionKey(o.value));
            return (
              <button
                type="button"
                key={o.value}
                className={`sites-mselect-row ${checked ? 'checked' : ''}`}
                onClick={() => toggle(o.value)}
              >
                <span className={`sites-mselect-box ${checked ? 'checked' : ''}`}>
                  {checked && <i className="fas fa-check" />}
                </span>
                <span className="sites-mselect-label">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formFromUser(u) {
  if (!u) return DEFAULT_FORM;
  const rawPhone = (u.phone || '').trim();
  const matchedCode = PHONE_CODES.find((code) => rawPhone.startsWith(code.value));
  const phoneCode = matchedCode?.value || '+1';
  const localPhone = matchedCode ? rawPhone.slice(phoneCode.length) : rawPhone;
  return {
    ...DEFAULT_FORM,
    firstName:     u.firstName || '',
    middleName:    u.middleName || '',
    lastName:      u.lastName || '',
    preferredName: u.preferredFirstName || u.preferredName || '',
    email:         u.email || '',
    phoneCode,
    phone:         localPhone,
    timezone:      u.timezone || '',
    startDate:     (u.startDate || '').slice(0, 10),
    endDate:       (u.endDate   || '').slice(0, 10),
    department:    u.department || '',
    departmentId:  u.departmentId || '',
    employeeId:    u.customerEmployeeId || '',
    externalId:    u.externalId || '',
    status:        u.status || 'ACTIVE',
    description:   u.description || '',
    pairingPin:    '',
  };
}

export default function UserDrawer({ open, user, onClose, onSaved }) {
  const { orgId, sites: tenantSites } = useTenant();
  const isEdit = !!user;

  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [assignments, setAssignments] = useState(DEFAULT_ASSIGNMENTS);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [talkGroups, setTalkGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [phoneCodeOpen, setPhoneCodeOpen] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const phoneCodeRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(formFromUser(user));
      setAssignments({
        ...DEFAULT_ASSIGNMENTS,
        siteIds:         (user?.siteIds && user.siteIds.length) ? user.siteIds : (user?.siteId ? [user.siteId] : []),
        roleIds:         (Array.isArray(user?.roleIds) && user.roleIds.length)
          ? normalizeRoleIds(user.roleIds)
          : normalizeRoleIds(user?.roleId ? [user.roleId] : []),
        teamIds:         user?.teamIds || (user?.teamId ? [user.teamId] : []),
        extraTalkGroupIds: user?.extraTalkGroupIds || [],
        homeTalkGroupId: user?.homeTalkGroupId || '',
      });
      setImagePreview(null);
      setTab('general');
      setTouched({});
      setError(null);
      setSubmitting(false);
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !isEdit || !orgId || !user?.employeeId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const fullUser = await userService.getUser(user.employeeId, orgId);
        if (cancelled || !fullUser) return;
        setForm((prev) => ({ ...formFromUser(fullUser), profileImage: prev.profileImage }));
      } catch (err) {
        // Fall back to list payload values already set by formFromUser(user).
        console.warn('Failed to load full user details for edit mode:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [open, isEdit, orgId, user?.employeeId]);

  useEffect(() => {
    if (!open || !isEdit || !orgId || !user?.employeeId) return undefined;

    let revokedUrl;
    let cancelled = false;

    (async () => {
      try {
        const imageBlob = await userService.getProfileImageBlob(user.employeeId, orgId);
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(imageBlob);
        revokedUrl = objectUrl;
        setImagePreview(objectUrl);
      } catch (err) {
        // 404 means no image uploaded yet; keep preview empty.
        const status = err?.response?.status;
        if (status !== 404) {
          console.warn('Failed to load existing profile image:', err);
        }
        if (!cancelled) {
          setImagePreview(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [open, isEdit, orgId, user?.employeeId]);

  useEffect(() => {
    if (!open || !orgId) return undefined;
    let cancelled = false;
    setSites(Array.isArray(tenantSites) ? tenantSites : []);
    (async () => {
      try {
        const [sitesRes, rolesRes, tgRes, teamsRes] = await Promise.all([
          organizationService.listSites(orgId, 0, 500).catch(() => []),
          employeeRoleService.listRoles(orgId).catch(() => []),
          organizationService.getTalkGroups(orgId).catch(() => []),
          teamsService.listTeams(orgId).catch(() => []),
        ]);
        if (cancelled) return;
        const siteList = Array.isArray(sitesRes) ? sitesRes : (sitesRes?.content || sitesRes?.items || []);
        setSites(Array.isArray(siteList) ? siteList : []);
        setRoles(Array.isArray(rolesRes) ? rolesRes : (rolesRes?.content || []));
        setTalkGroups(Array.isArray(tgRes) ? tgRes : (tgRes?.content || []));
        setTeams(Array.isArray(teamsRes) ? teamsRes : (teamsRes?.content || []));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, orgId, tenantSites]);

  useEffect(() => {
    if (!open || !orgId || !isEdit || !user?.employeeId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const roleIdsPayload = await employeeRoleService.listRolesForUser(user.employeeId, orgId);
        if (cancelled) return;
        setAssignments((prev) => ({
          ...prev,
          roleIds: normalizeRoleIdsResponse(roleIdsPayload),
        }));
      } catch {
        // Keep existing fallback from user.roleId if role list lookup fails.
      }
    })();
    return () => { cancelled = true; };
  }, [open, orgId, isEdit, user?.employeeId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!phoneCodeOpen) return undefined;
    const onDoc = (e) => {
      if (phoneCodeRef.current && !phoneCodeRef.current.contains(e.target)) setPhoneCodeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [phoneCodeOpen]);

  if (!open) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setA = (key) => (val) => setAssignments((prev) => ({ ...prev, [key]: val }));
  const onInput = (key) => (e) => setField(key, e.target.value);
  const markTouched = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const normalizeIdArray = (ids) => (Array.isArray(ids)
    ? ids.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    : []);

  const errs = {
    firstName:  !form.firstName.trim()  ? 'This field is required' : null,
    lastName:   !form.lastName.trim()   ? 'This field is required' : null,
    email:      !form.email.trim()      ? 'This field is required' : null,
    employeeId: !form.employeeId.trim() ? 'This field is required' : null,
  };
  const hasErrors = Object.values(errs).some(Boolean);

  const handleImagePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setField('profileImage', f);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };
  const clearImage = () => { setField('profileImage', null); setImagePreview(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, employeeId: true });
    if (hasErrors || submitting || !orgId) return;
    setSubmitting(true);
    setError(null);
    try {
      const normalizedRoleIds = normalizeIdArray(assignments.roleIds);
      const normalizedPhone = form.phone.trim();
      const phoneValue = !normalizedPhone
        ? undefined
        : (normalizedPhone.startsWith('+') ? normalizedPhone : `${form.phoneCode}${normalizedPhone}`);
      const payload = {
        orgId,
        email:              form.email.trim(),
        firstName:          form.firstName.trim(),
        middleName:         form.middleName.trim() || undefined,
        lastName:           form.lastName.trim(),
        preferredFirstName: form.preferredName.trim() || undefined,
        phone:              phoneValue,
        timezone:           form.timezone || undefined,
        startDate:          form.startDate || undefined,
        endDate:            form.endDate   || undefined,
        department:         form.department.trim()   || undefined,
        departmentId:       form.departmentId.trim() || undefined,
        externalId:         form.externalId.trim()   || undefined,
        status:             form.status,
        description:        form.description.trim()  || undefined,
        siteId:             assignments.siteIds[0]   || undefined,
        siteIds:            assignments.siteIds.length ? assignments.siteIds : undefined,
        roleId:             normalizedRoleIds[0]   || undefined,
        teamId:             assignments.teamIds[0]   || undefined,
        teamIds:            assignments.teamIds.length ? assignments.teamIds : undefined,
        extraTalkGroupIds:  assignments.extraTalkGroupIds.length ? assignments.extraTalkGroupIds : undefined,
        homeTalkGroupId:    assignments.homeTalkGroupId || undefined,
        pairingPin:         form.pairingPin.trim()   || undefined,
      };
      let savedUserId;
      if (isEdit) {
        savedUserId = user.employeeId;
        await userService.updateUser(user.employeeId, payload, orgId);
        await employeeRoleService.assignRolesForUser(user.employeeId, normalizedRoleIds, orgId);
        // Assign departments/teams if any selected
        const normalizedTeamIds = normalizeIdArray(assignments.teamIds);
        if (normalizedTeamIds.length > 0) {
          await userService.assignDepartments(user.employeeId, normalizedTeamIds, orgId);
        }
      } else {
        const created = await userService.createUser({ ...payload, customerEmployeeId: form.employeeId.trim() });
        const createdUserId = created?.employeeId ?? created?.userId;
        savedUserId = createdUserId;
        if (createdUserId != null) {
          await employeeRoleService.assignRolesForUser(createdUserId, normalizedRoleIds, orgId);
          // Assign departments/teams if any selected
          const normalizedTeamIds = normalizeIdArray(assignments.teamIds);
          if (normalizedTeamIds.length > 0) {
            await userService.assignDepartments(createdUserId, normalizedTeamIds, orgId);
          }
        }
      }
      // Upload profile image if one was selected
      if (form.profileImage && savedUserId) {
        try {
          await userService.uploadProfileImage(savedUserId, form.profileImage, orgId);
        } catch (imgErr) {
          console.warn('Profile image upload failed:', imgErr);
        }
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? err.message
        ?? (isEdit ? 'Failed to update user' : 'Failed to create user'),
      );
      setSubmitting(false);
    }
  };

  const siteOptions = (sites || []).map((s) => ({
    value: s.siteId ?? s.id,
    label: s.siteName ?? s.name ?? `Site ${s.siteId ?? s.id}`,
  }));
  const roleOptions = (roles || []).map((r) => ({
    value: r.roleId ?? r.id,
    label: r.roleName ?? r.name ?? `Role ${r.roleId ?? r.id}`,
  }));
  const tgOptions = (talkGroups || []).map((g) => ({
    value: g.talkGroupId ?? g.id,
    label: g.name ?? g.talkGroupName ?? `Group ${g.talkGroupId ?? g.id}`,
  }));
  const teamOptions = (teams || []).map((t) => ({
    value: t.teamId ?? t.id,
    label: t.teamName ?? t.name ?? `Team ${t.teamId ?? t.id}`,
  }));

  const errCls  = (key) => (touched[key] && errs[key]) ? 'os-input os-input-error' : 'os-input';
  const showErr = (key) => touched[key] && errs[key];

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit user' : 'Add user'}
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="os-modal um-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit user' : 'Add user'}</h2>
        </header>

        <div className="os-modal-body">
          <aside className="os-side">
            <button
              type="button"
              className={`os-side-item ${tab === 'general' ? 'active' : ''}`}
              onClick={() => setTab('general')}
            >
              <i className="fas fa-user" />
              <span>General settings</span>
            </button>
            <button
              type="button"
              className={`os-side-item ${tab === 'assignments' ? 'active' : ''}`}
              onClick={() => setTab('assignments')}
            >
              <i className="fas fa-user-tag" />
              <span>Assignments</span>
            </button>
          </aside>

          <section className="os-pane um-pane">
            {tab === 'general' && (
              <div className="um-grid">
                <div className="um-field">
                  <label className="os-label">First name<span className="os-req">*</span></label>
                  <input
                    className={errCls('firstName')}
                    placeholder="First name"
                    value={form.firstName}
                    onChange={onInput('firstName')}
                    onBlur={() => markTouched('firstName')}
                  />
                  {showErr('firstName') && <div className="os-field-error"><i className="fas fa-info-circle" /> {errs.firstName}</div>}
                </div>

                <div className="um-field um-image-cell" style={{ gridRow: 'span 4' }}>
                  <label className="os-label">User profile image</label>
                  <div
                    className="um-image-tile"
                    role="button"
                    tabIndex={0}
                    onClick={() => fileRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
                  >
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Profile preview" />
                        <button
                          type="button"
                          className="um-image-clear"
                          onClick={(e) => { e.stopPropagation(); clearImage(); }}
                          aria-label="Remove image"
                        >
                          <i className="fas fa-times" />
                        </button>
                      </>
                    ) : (
                      <div className="um-image-placeholder">
                        <span className="um-image-add"><i className="fas fa-plus" /></span>
                        <span className="um-image-label">Add image</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImagePick}
                  />
                </div>

                <div className="um-field">
                  <label className="os-label">Middle name</label>
                  <input className="os-input" placeholder="Middle name" value={form.middleName} onChange={onInput('middleName')} />
                </div>

                <div className="um-field">
                  <label className="os-label">Last name<span className="os-req">*</span></label>
                  <input
                    className={errCls('lastName')}
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={onInput('lastName')}
                    onBlur={() => markTouched('lastName')}
                  />
                  {showErr('lastName') && <div className="os-field-error"><i className="fas fa-info-circle" /> {errs.lastName}</div>}
                </div>

                <div className="um-field">
                  <label className="os-label">Preferred name</label>
                  <input className="os-input" placeholder="Preferred name" value={form.preferredName} onChange={onInput('preferredName')} />
                </div>

                <div className="um-field um-col-span-2">
                  <label className="os-label">Email<span className="os-req">*</span></label>
                  <input
                    type="email"
                    className={errCls('email')}
                    placeholder="name@company.com"
                    value={form.email}
                    onChange={onInput('email')}
                    onBlur={() => markTouched('email')}
                  />
                  {showErr('email') && <div className="os-field-error"><i className="fas fa-info-circle" /> {errs.email}</div>}
                </div>

                <div className="um-field">
                  <label className="os-label">Phone number</label>
                  <div className="um-phone">
                    <div className="um-phone-code" ref={phoneCodeRef}>
                      <button
                        type="button"
                        className="um-phone-code-btn"
                        onClick={() => setPhoneCodeOpen((o) => !o)}
                      >
                        {PHONE_CODES.find((c) => c.value === form.phoneCode)?.label || form.phoneCode}
                        <i className={`fas fa-chevron-${phoneCodeOpen ? 'up' : 'down'}`} />
                      </button>
                      {phoneCodeOpen && (
                        <div className="um-phone-code-menu">
                          {PHONE_CODES.map((c) => (
                            <button
                              type="button"
                              key={c.value}
                              className="um-phone-code-item"
                              onClick={() => { setField('phoneCode', c.value); setPhoneCodeOpen(false); }}
                            >
                              {c.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      className="os-input"
                      placeholder="eg. 1111111111"
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value.replace(/[^0-9\s-]/g, ''))}
                    />
                  </div>
                </div>

                <div className="um-field">
                  <label className="os-label">Time zone</label>
                  <select className="os-input os-select" value={form.timezone} onChange={onInput('timezone')}>
                    {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div className="um-field">
                  <label className="os-label">Start date/time</label>
                  <input type="date" className="os-input" value={form.startDate} onChange={onInput('startDate')} />
                </div>
                <div className="um-field">
                  <label className="os-label">End date/time</label>
                  <input type="date" className="os-input" value={form.endDate} onChange={onInput('endDate')} />
                </div>

                <div className="um-field">
                  <label className="os-label">Department</label>
                  <input className="os-input" placeholder="Department" value={form.department} onChange={onInput('department')} />
                </div>
                <div className="um-field">
                  <label className="os-label">Department ID</label>
                  <input className="os-input" placeholder="Department ID" value={form.departmentId} onChange={onInput('departmentId')} />
                </div>

                <div className="um-field">
                  <label className="os-label">Employee ID<span className="os-req">*</span></label>
                  <input
                    className={errCls('employeeId')}
                    placeholder="Employee ID"
                    value={form.employeeId}
                    onChange={onInput('employeeId')}
                    onBlur={() => markTouched('employeeId')}
                    disabled={isEdit}
                    title={isEdit ? 'Employee ID cannot be changed after creation' : undefined}
                  />
                  {showErr('employeeId') && <div className="os-field-error"><i className="fas fa-info-circle" /> {errs.employeeId}</div>}
                </div>
                <div className="um-field">
                  <label className="os-label">External ID</label>
                  <input className="os-input" placeholder="External ID" value={form.externalId} onChange={onInput('externalId')} />
                </div>

                <div className="um-field um-col-span-2">
                  <label className="os-label">Status</label>
                  <select className="os-input os-select" value={form.status} onChange={onInput('status')}>
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>

                <div className="um-field um-col-span-2">
                  <label className="os-label">Description</label>
                  <textarea
                    className="os-input os-textarea"
                    rows={4}
                    placeholder="Display if they have"
                    value={form.description}
                    onChange={onInput('description')}
                  />
                </div>
              </div>
            )}

            {tab === 'assignments' && (
              <div className="um-assign">
                <section className="um-card">
                  <label className="um-card-label">Site associations <HelpIcon /></label>
                  <MultiSelect
                    placeholder="Select site associations"
                    caption="SELECT SITES FROM THE LIST"
                    selectAllLabel="Select all sites"
                    options={siteOptions}
                    value={assignments.siteIds}
                    onChange={setA('siteIds')}
                  />
                </section>

                <section className="um-card">
                  <label className="um-card-label">Roles <HelpIcon /></label>
                  <div className="um-card-hint">
                    At least one role must be added in order to allow portal access
                  </div>
                  <MultiSelect
                    placeholder="Select roles"
                    caption="SELECT ROLES FROM THE LIST"
                    selectAllLabel="Select all roles"
                    options={roleOptions}
                    value={assignments.roleIds}
                    onChange={setA('roleIds')}
                  />
                </section>

                <section className="um-card">
                  <label className="um-card-label">Teams <HelpIcon /></label>
                  <MultiSelect
                    placeholder="Select teams"
                    caption="SELECT TEAMS FROM THE LIST"
                    selectAllLabel="Select all teams"
                    options={teamOptions}
                    value={assignments.teamIds}
                    onChange={setA('teamIds')}
                  />
                </section>

                <section className="um-card">
                  <label className="um-card-label">Talk groups <HelpIcon /></label>
                  <MultiSelect
                    placeholder="Select talk groups"
                    caption="SELECT TALK GROUPS FROM THE LIST"
                    selectAllLabel="Select all talk groups"
                    options={tgOptions}
                    value={assignments.extraTalkGroupIds}
                    onChange={setA('extraTalkGroupIds')}
                  />
                </section>

                <section className="um-card">
                  <label className="um-card-label">Home group <HelpIcon /></label>
                  <MultiSelect
                    placeholder="Select home group"
                    caption="SELECT A HOME GROUP"
                    options={tgOptions}
                    value={assignments.homeTalkGroupId}
                    onChange={setA('homeTalkGroupId')}
                    single
                  />
                </section>
              </div>
            )}
          </section>
        </div>

        {error && <div className="os-modal-error">{error}</div>}

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}
